import 'animate.css';

import { Check, ContentCopy } from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Grid, Radio } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { Checkbox, HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Project } from '../../../../../domain/project/Project';
import {
  MAX_AVATAR_IMAGE_SIZE,
  MAX_FAVICON_SIZE,
  UPLOAD_TYPES,
  generateAttachmentName,
} from '../../../../../helpers/Images';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useCreateProjectStyleImage } from '../../../../../hooks/project/useCreateProjectStyleImage';
import { useDeleteProjectStyleImage } from '../../../../../hooks/project/useDeleteProjectStyleImage';
import { useUpdateProject } from '../../../../../hooks/project/useUpdateProject';
import { useRetrieveSupportDocs } from '../../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../../../hooks/user/useRetrieveUserCredentials';
import { ProjectSupervisorsStyleMapper } from '../../../../../mappers/project/ProjectSupervisorsStyleMapper';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';
import configuration from '../../../../../services/configuration';
import { getUploadAsBase64 } from '../../../../../services/requests';
import { StylingColorPicker } from '../../../../shared/styling/StylingColorPicker';
import { StylingFaviconManager } from '../../../../shared/styling/StylingFaviconManager';
import { StylingImageManager } from '../../../../shared/styling/StylingImageManager';

interface SupervisorsStylingConfigurationProps {
  project: Project;
}

export const SupervisorsStylingConfiguration = ({ project }: SupervisorsStylingConfigurationProps) => {
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { run: editProject } = useUpdateProject({ project });
  const { run: uploadStyleImage } = useCreateProjectStyleImage({ project });
  const { run: removeStyleImage } = useDeleteProjectStyleImage({ project });
  const [pageTitle, setPageTitle] = useState<string>(``);
  const [projectSlug, setProjectSlug] = useState<string>(``);
  const [customDomain, setCustomDomain] = useState<string>(``);
  const [copyDefaultUrlStatus, setCopyDefaultUrlStatus] = useState<`copied` | `copy`>(`copy`);
  const [copyCustomDomainStatus, setCopyCustomDomainStatus] = useState<`copied` | `copy`>(`copy`);

  useEffect(() => {
    setPageTitle(project.supervisors.style.page_title ?? ``);
  }, [project.supervisors.style.page_title]);

  useEffect(() => {
    setProjectSlug(project.project_slug ?? ``);
  }, [project.project_slug]);

  useEffect(() => {
    setCustomDomain(project.supervisors.style.custom_domain ?? ``);
  }, [project.supervisors.style.custom_domain]);

  const handleCustomDomain = (customDomain: string) => {
    const el = document.createElement(`textarea`);
    el.value = customDomain;
    const helper = document.getElementById(`copy-custom_domain-helper`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.appendChild(el);
    el.select();
    document.execCommand(`copy`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.removeChild(el);

    setCopyCustomDomainStatus(`copied`);

    setTimeout(() => {
      setCopyCustomDomainStatus(`copy`);
    }, 2000);
  };

  const handleCopyDefaultUrl = (defaultUrl: string) => {
    const el = document.createElement(`textarea`);
    el.value = defaultUrl;
    const helper = document.getElementById(`copy-default_url-helper`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.appendChild(el);
    el.select();
    document.execCommand(`copy`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.removeChild(el);

    setCopyDefaultUrlStatus(`copied`);

    setTimeout(() => {
      setCopyDefaultUrlStatus(`copy`);
    }, 2000);
  };

  const isDefaultUrlInvalid = (text?: string) => {
    const expression1 = /^[a-z0-9]+$/;

    const regex1 = new RegExp(expression1);
    if ((text ?? ``) !== `` && (text ?? ``).match(regex1)) {
      return false;
    }
    return true;
  };

  const isCustomDomainInvalid = (text?: string) => {
    const expression1 = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;

    if ((text ?? ``) === ``) {
      return false;
    }

    const regex1 = new RegExp(expression1);
    if ((text ?? ``) !== `` && (text ?? ``).match(regex1)) {
      return false;
    }
    return true;
  };

  const hasWhiteLabelSubscription = userCredentials?.subscription.test(`supervisors.white_label`);

  return (
    <>
      {!hasWhiteLabelSubscription ? (
        <Grid
          container
          wrap="nowrap"
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ padding: `0 12px`, backgroundColor: `#5073B5`, width: `100%`, minHeight: `66px` }}
        >
          <Grid
            item
            style={{ borderRadius: `50%`, backgroundColor: RoutalPalette.neutral00, width: `24px`, height: `24px` }}
          >
            <ArrowUpwardIcon style={{ color: `#8ca3cf` }} />
          </Grid>
          <HSpacer small />
          <Text
            variant="body-medium"
            style={{ color: RoutalPalette.neutral00 }}
            intlId="project.settings.styling.under_gorilla"
          />
        </Grid>
      ) : null}

      <Grid container direction="column" style={{ padding: `12px` }}>
        <Grid item container direction="column" wrap="nowrap">
          <Text intlId="project.settings.styling.default_url" />

          <VSpacer small />

          <InputText
            endAdornment={
              <span
                style={{
                  borderRadius: `2px`,
                  padding: `4px 6px`,
                  backgroundColor: RoutalPalette.grey8,
                  marginRight: `4px`,
                }}
              >
                {`.${configuration.SUPERVISORS_HOST}`}
              </span>
            }
            fieldValue={projectSlug}
            onChange={(value) => {
              if ((value ?? ``) === ``) {
                setProjectSlug(project.id);
                return;
              }
              setProjectSlug(value.toLowerCase());
            }}
            validators={[
              {
                errorMessage: (
                  <Text
                    tagName="span"
                    style={{ cursor: `default`, color: RoutalPalette.error.medium }}
                    intlId="project.settings.styling.default_url.invalid"
                  />
                ),
                isInvalid: (text?: string) => isDefaultUrlInvalid(text),
              },
            ]}
            onBlur={(e: any) => {
              if ((project.project_slug ?? ``) === projectSlug || isDefaultUrlInvalid(projectSlug)) return;
              editProject({ project_slug: projectSlug });
            }}
          />

          <VSpacer small />

          <Text
            intlId={`project.settings.styling.default_url.${copyDefaultUrlStatus === `copy` ? `copy` : `copied`}`}
            onClick={(e: any) => {
              handleCopyDefaultUrl(`${projectSlug}.${configuration.SUPERVISORS_HOST}`);
            }}
            variant="button"
            style={{
              ...(copyDefaultUrlStatus === `copied` ? { color: RoutalPalette.statusColors.completed } : {}),
            }}
          />

          <div id="copy-default_url-helper" />
        </Grid>

        <VSpacer large />

        <Grid item container direction="column" wrap="nowrap">
          <div id="copy-custom_domain-helper" />

          <Text
            style={{
              color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
            }}
            intlId="project.settings.styling.custom_domain"
          />

          <VSpacer small />

          <InputText
            disabled={!hasWhiteLabelSubscription}
            endAdornment={
              <Button
                disabled={!hasWhiteLabelSubscription || !project.customers.style.custom_domain}
                style={{ borderRadius: `4px` }}
                onClick={(e: any) => {
                  handleCustomDomain(project.supervisors.style.custom_domain ?? ``);
                }}
              >
                {copyCustomDomainStatus === `copy` ? (
                  <ContentCopy
                    style={{
                      fontSize: `16px`,
                      color: RoutalPalette.neutral00,
                      cursor:
                        !hasWhiteLabelSubscription || !project.supervisors.style.custom_domain ? `default` : `pointer`,
                    }}
                  />
                ) : (
                  <Check
                    style={{
                      fontSize: `16px`,
                      color: RoutalPalette.neutral00,
                    }}
                  />
                )}
              </Button>
            }
            validators={[
              {
                errorMessage: (
                  <Text
                    tagName="span"
                    style={{ cursor: `default`, color: RoutalPalette.error.medium }}
                    intlId="project.settings.styling.custom_domain.invalid"
                  />
                ),
                isInvalid: (text?: string) => isCustomDomainInvalid(text),
              },
            ]}
            placeholderIntlId="project.settings.styling.custom_domain_placeholder"
            fieldValue={project.supervisors.style.custom_domain ?? ``}
            onChange={(value) => {
              setCustomDomain(value);
            }}
            onBlur={(e: any) => {
              if ((project.supervisors.style.custom_domain ?? ``) === customDomain) return;

              const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
              projectStyle.custom_domain = customDomain;
              project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
              editProject({ supervisors: project.supervisors });
            }}
          />

          <VSpacer small />

          <Text
            intlId="project.settings.styling.custom_domain.how_does_it_work"
            variant="button"
            style={
              !hasWhiteLabelSubscription
                ? {
                    color: RoutalPalette.neutral20,
                    cursor: `default`,
                  }
                : undefined
            }
            onClick={() => {
              if (hasWhiteLabelSubscription) {
                window.open(
                  retrieveDoc({ supportPage: `communicationCustomers`, language: user?.language.value }),
                  `_blank`
                );
              }
            }}
          />
        </Grid>

        <VSpacer large />

        <Grid item container direction="column" wrap="nowrap">
          <Text
            style={{
              color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
            }}
            intlId="project.settings.styling.page_title"
          />

          <VSpacer small />

          <InputText
            disabled={!hasWhiteLabelSubscription}
            fieldValue={project.supervisors.style.page_title ?? ``}
            onChange={(value) => {
              setPageTitle(value);
            }}
            onBlur={(e: any) => {
              if ((project.supervisors.style.page_title ?? ``) === pageTitle) return;

              const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
              projectStyle.page_title = pageTitle;
              project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
              editProject({ supervisors: project.supervisors });
            }}
          />
        </Grid>

        <VSpacer large />

        <Grid item container direction="column" wrap="nowrap">
          <Text
            style={{
              color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
            }}
            intlId="project.settings.styling.favicon"
          />

          <VSpacer small />

          <StylingFaviconManager
            disabled={!hasWhiteLabelSubscription}
            id="upload_supervisors_style_favicon"
            avatarUrl={project.supervisors.style.favicon}
            fileName={generateAttachmentName(UPLOAD_TYPES.PROJECT_STYLE_FAVICON)}
            maxFileSize={MAX_FAVICON_SIZE}
            confirmText={intl.formatMessage({ id: `confirm` })}
            cancelText={intl.formatMessage({ id: `cancel` })}
            onRemoveAvatar={() => removeStyleImage({ imageType: `favicon`, domain: `supervisors` })}
            onAvatarUpload={(fileName: string, mimeType: string, file: Blob) =>
              uploadStyleImage({ fileName, mimeType, imageType: `favicon`, file, domain: `supervisors` })
            }
            getUploadAsBase64={getUploadAsBase64}
          />
        </Grid>

        <VSpacer large />

        <Grid item container direction="column" wrap="nowrap">
          <Text
            style={{
              color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
            }}
            intlId="project.settings.styling.logo"
          />

          <VSpacer small />

          <StylingImageManager
            disabled={!hasWhiteLabelSubscription}
            id="upload_supervisors_style_logo"
            avatarUrl={project.supervisors.style.logo}
            fileName={generateAttachmentName(UPLOAD_TYPES.PROJECT_STYLE_LOGO)}
            maxFileSize={MAX_AVATAR_IMAGE_SIZE}
            confirmText={intl.formatMessage({ id: `confirm` })}
            cancelText={intl.formatMessage({ id: `cancel` })}
            onRemoveAvatar={() => removeStyleImage({ imageType: `logo`, domain: `supervisors` })}
            onAvatarUpload={(fileName: string, mimeType: string, file: Blob) =>
              uploadStyleImage({ fileName, mimeType, imageType: `logo`, file, domain: `supervisors` })
            }
            getUploadAsBase64={getUploadAsBase64}
          />
        </Grid>

        <VSpacer large />

        <Grid item container direction="column">
          <Grid item>
            <Text
              style={{
                color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
              }}
              intlId="project.settings.styling.banner"
            />
          </Grid>

          <VSpacer large />

          <Grid item container direction="column">
            <Grid item container direction="row" alignItems="center" wrap="nowrap">
              <Grid item xs={2}>
                <Radio
                  disabled={!hasWhiteLabelSubscription}
                  checked={project.supervisors.style.banner_type === `image`}
                  onChange={(event: any, checked: boolean) => {
                    const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
                    projectStyle.banner_type = `image`;
                    project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
                    editProject({ supervisors: project.supervisors });
                  }}
                />
              </Grid>

              <Grid item xs={10}>
                <Text
                  style={{
                    color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
                  }}
                  intlId="project.settings.styling.banner.image"
                />
              </Grid>
            </Grid>

            {project.supervisors.style.banner_type === `image` ? (
              <Grid
                item
                container
                direction="row"
                alignItems="center"
                className="animate__animated animate__fadeIn animate__fast"
              >
                <Grid item xs={2} />
                <Grid item xs={10}>
                  <StylingImageManager
                    disabled={!hasWhiteLabelSubscription}
                    id="upload_supervisors_style_banner"
                    avatarUrl={project.supervisors.style.banner_image}
                    fileName={generateAttachmentName(UPLOAD_TYPES.PROJECT_STYLE_BANNER)}
                    maxFileSize={MAX_AVATAR_IMAGE_SIZE}
                    confirmText={intl.formatMessage({ id: `confirm` })}
                    cancelText={intl.formatMessage({ id: `cancel` })}
                    onRemoveAvatar={() => removeStyleImage({ imageType: `banner`, domain: `supervisors` })}
                    onAvatarUpload={(fileName: string, mimeType: string, file: Blob) =>
                      uploadStyleImage({ fileName, mimeType, imageType: `banner`, file, domain: `supervisors` })
                    }
                    getUploadAsBase64={getUploadAsBase64}
                  />
                </Grid>
              </Grid>
            ) : null}
          </Grid>

          <Grid item container direction="column">
            <Grid item container direction="row" alignItems="center" wrap="nowrap">
              <Grid item xs={2}>
                <Radio
                  disabled={!hasWhiteLabelSubscription}
                  checked={project.supervisors.style.banner_type === `color`}
                  onChange={(event: any, checked: boolean) => {
                    const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
                    projectStyle.banner_type = `color`;
                    project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
                    editProject({ supervisors: project.supervisors });
                  }}
                />
              </Grid>

              <Grid item xs={10}>
                <Text
                  style={{
                    color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
                  }}
                  intlId="project.settings.styling.banner.color"
                />
              </Grid>
            </Grid>

            {project.supervisors.style.banner_type === `color` ? (
              <Grid
                item
                container
                direction="row"
                alignItems="center"
                className="animate__animated animate__fadeIn animate__fast"
              >
                <Grid item xs={2} />
                <Grid item xs={10}>
                  <StylingColorPicker
                    disabled={!hasWhiteLabelSubscription}
                    color={project.supervisors.style.banner_color ?? HexColor.create(RoutalPalette.secondary40)}
                    inputContainerStyles={{
                      width: `202px`,
                    }}
                    onChange={(color: HexColor) => {
                      const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
                      projectStyle.banner_color = color.value;
                      project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
                      editProject({ supervisors: project.supervisors });
                    }}
                  />
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        </Grid>

        <VSpacer large />

        <Grid item>
          <StylingColorPicker
            disabled={!hasWhiteLabelSubscription}
            label={intl.formatMessage({ id: `project.settings.styling.primary_color` })}
            color={
              project.supervisors.style.primary_color ?? HexColor.create(RoutalPalette.button.primary.defaultColor)
            }
            onChange={(color: HexColor) => {
              const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
              projectStyle.primary_color = color.value;
              project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
              editProject({ supervisors: project.supervisors });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <StylingColorPicker
            disabled={!hasWhiteLabelSubscription}
            label={intl.formatMessage({ id: `project.settings.styling.secondary_color` })}
            color={project.supervisors.style.secondary_color ?? HexColor.create(RoutalPalette.secondary40)}
            onChange={(color: HexColor) => {
              const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
              projectStyle.secondary_color = color.value;
              project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
              editProject({ supervisors: project.supervisors });
            }}
          />
        </Grid>
        <VSpacer medium />
        <Grid item>
          <Text
            style={{
              color: !hasWhiteLabelSubscription ? RoutalPalette.neutral20 : RoutalPalette.secondary40,
            }}
            intlId="project.settings.styling.show_powered_by"
          />

          <VSpacer small />

          <Checkbox
            disabled={!hasWhiteLabelSubscription}
            checked={project.supervisors.style.show_powered_by}
            onChange={(event) => {
              const projectStyle = ProjectSupervisorsStyleMapper.toDTO(project.supervisors.style);
              projectStyle.show_powered_by = event.target.checked;
              project.supervisors.style = ProjectSupervisorsStyleMapper.toDomain(projectStyle);
              editProject({ supervisors: project.supervisors });
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
